/**
 * Dispute Drawer — Phase 1c.2
 *
 * Lists disputes with open/withdraw/resolve actions. Filing a dispute does
 * NOT auto-pause a holdback — operator must explicitly link via the
 * Holdback Panel "Pause for dispute" action.
 */
import { useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAppData } from "@/data/AppDataContext";
import {
  openDispute,
  withdrawDispute,
  resolveDispute,
  deriveAllDisputes,
  type DisputeOutcome,
  type DisputeState,
} from "@/data/contractor/networkExecution";
import { useRole } from "@/data/RoleContext";
import { toast } from "sonner";

type DialogKind = "open" | "withdraw" | "resolve";
const RESOLVE_OUTCOMES: Exclude<DisputeOutcome, "withdrawn">[] = [
  "upheld",
  "overturned",
  "partial",
];

interface Props {
  /** When set, only this contractor's disputes are shown. */
  contractorId?: string;
  readOnly?: boolean;
}

export function DisputeDrawer({ contractorId, readOnly }: Props) {
  const { disputeEvents, setDisputeEvents } = useAppData();
  const { role } = useRole();

  const states = useMemo(() => {
    const all = deriveAllDisputes(disputeEvents);
    return contractorId ? all.filter((d) => d.contractorId === contractorId) : all;
  }, [disputeEvents, contractorId]);

  const [dialog, setDialog] = useState<{ kind: DialogKind; state?: DisputeState } | null>(
    null,
  );
  const [form, setForm] = useState({
    jobId: "",
    contractorId: contractorId ?? "",
    callbackId: "",
    holdbackId: "",
    reason: "",
    outcome: "upheld" as Exclude<DisputeOutcome, "withdrawn">,
  });
  const [error, setError] = useState<string | null>(null);

  const open = (kind: DialogKind, state?: DisputeState) => {
    setDialog({ kind, state });
    setError(null);
    setForm({
      jobId: state?.jobId ?? "",
      contractorId: state?.contractorId ?? contractorId ?? "",
      callbackId: state?.callbackId ?? "",
      holdbackId: state?.holdbackId ?? "",
      reason: "",
      outcome: "upheld",
    });
  };

  const submit = () => {
    if (!dialog) return;
    setError(null);
    let result;
    if (dialog.kind === "open") {
      result = openDispute({
        events: disputeEvents,
        jobId: form.jobId,
        contractorId: form.contractorId,
        callbackId: form.callbackId || undefined,
        holdbackId: form.holdbackId || undefined,
        actorId: role,
        actorRole: role,
        reason: form.reason,
      });
    } else if (dialog.kind === "withdraw" && dialog.state) {
      result = withdrawDispute({
        events: disputeEvents,
        disputeId: dialog.state.disputeId,
        jobId: dialog.state.jobId,
        contractorId: dialog.state.contractorId,
        actorId: role,
        actorRole: role,
        reason: form.reason,
      });
    } else if (dialog.kind === "resolve" && dialog.state) {
      result = resolveDispute({
        events: disputeEvents,
        disputeId: dialog.state.disputeId,
        jobId: dialog.state.jobId,
        contractorId: dialog.state.contractorId,
        actorId: role,
        actorRole: role,
        reason: form.reason,
        outcome: form.outcome,
      });
    }
    if (!result?.event) {
      setError(result?.error ?? "Could not record event.");
      return;
    }
    setDisputeEvents(result.events);
    toast.success("Dispute event recorded.");
    setDialog(null);
  };

  return (
    <Card className="p-3 space-y-3" data-testid="dispute-drawer">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold">Disputes</h3>
          <p className="text-[11px] text-muted-foreground">
            Filing a dispute does not pause holdback release. Use the Holdback panel to
            manually link.
          </p>
        </div>
        {!readOnly && (
          <Button size="sm" variant="outline" onClick={() => open("open")}>
            File dispute
          </Button>
        )}
      </div>

      {states.length === 0 ? (
        <div className="text-xs text-muted-foreground">No disputes filed.</div>
      ) : (
        <ul className="space-y-2" data-testid="dispute-list">
          {states.map((d) => (
            <li
              key={d.disputeId}
              className="rounded-md border border-border/60 p-2.5 text-xs space-y-1.5"
            >
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-medium font-mono">{d.disputeId}</div>
                  <div className="text-muted-foreground">
                    Job {d.jobId}
                    {d.callbackId ? ` • Callback ${d.callbackId}` : ""}
                    {d.holdbackId ? ` • Holdback ${d.holdbackId}` : ""}
                  </div>
                </div>
                <Badge
                  variant={
                    d.status === "open"
                      ? "destructive"
                      : d.status === "resolved"
                      ? "secondary"
                      : "outline"
                  }
                  className="text-[10px] capitalize"
                >
                  {d.status}
                  {d.outcome ? ` • ${d.outcome}` : ""}
                </Badge>
              </div>
              {!readOnly && d.status === "open" && (
                <div className="flex flex-wrap gap-1.5 pt-1">
                  <Button size="sm" variant="outline" onClick={() => open("resolve", d)}>
                    Resolve
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => open("withdraw", d)}>
                    Withdraw
                  </Button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      <Dialog open={!!dialog} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {dialog?.kind === "open"
                ? "File dispute"
                : dialog?.kind === "withdraw"
                ? "Withdraw dispute"
                : "Resolve dispute"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {dialog?.kind === "open" && (
              <>
                <div>
                  <label className="text-xs font-medium">Job ID</label>
                  <Input
                    value={form.jobId}
                    onChange={(e) => setForm({ ...form, jobId: e.target.value })}
                  />
                </div>
                <div>
                  <label className="text-xs font-medium">Contractor ID</label>
                  <Input
                    value={form.contractorId}
                    onChange={(e) => setForm({ ...form, contractorId: e.target.value })}
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs font-medium">Callback ID (optional)</label>
                    <Input
                      value={form.callbackId}
                      onChange={(e) => setForm({ ...form, callbackId: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium">Holdback ID (optional)</label>
                    <Input
                      value={form.holdbackId}
                      onChange={(e) => setForm({ ...form, holdbackId: e.target.value })}
                    />
                  </div>
                </div>
              </>
            )}
            {dialog?.kind === "resolve" && (
              <div>
                <label className="text-xs font-medium">Outcome</label>
                <div className="mt-1 flex gap-2">
                  {RESOLVE_OUTCOMES.map((o) => (
                    <button
                      key={o}
                      type="button"
                      onClick={() => setForm({ ...form, outcome: o })}
                      className={`px-3 py-1 rounded-md text-xs border capitalize ${
                        form.outcome === o
                          ? "bg-primary/15 text-primary ring-1 ring-primary/30"
                          : "bg-card hover:bg-muted"
                      }`}
                    >
                      {o}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div>
              <label className="text-xs font-medium">Reason (≥ 10 characters)</label>
              <Textarea
                value={form.reason}
                onChange={(e) => setForm({ ...form, reason: e.target.value })}
                rows={3}
              />
            </div>
            {error && <div className="text-xs text-destructive">{error}</div>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)}>
              Cancel
            </Button>
            <Button onClick={submit}>Record</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
